import { DataBarVertical32Regular } from '@fluentui/react-icons';
import { useI18nTranslation } from '@idmc-ms-fabric/common';
import { useFabricContext } from '@idmc-ms-fabric/integration';
import { createContext, FC, useContext, useEffect, useMemo, useReducer } from 'react';
import { useParams } from 'react-router-dom';

import ErrorScreen from '../../../component/core/error/ErrorScreen';
import ProgressScreen from '../../../component/core/progress/ProgressScreen';
import i18nKeys from '../../../i18n/i18nKeys';
import { API_STATUS } from '../../../utils/common-types';
import { IDQAPageState } from './IDQAPage.Types';
import IDQAPageController from './IDQAPageController';
import IDQAPageReducer from './IDQAPageReducer';

const initState: IDQAPageState = {
	status: API_STATUS.LOADING,
	runningJobDetails: { filters: [] },
};
type ContextType = { state: IDQAPageState; controller: IDQAPageController };

const Context = createContext<ContextType>({
	state: initState,
	controller: {} as IDQAPageController,
});

export const useIDQAPageContext = (): ContextType => useContext(Context);

const IDQAPageContext: FC = ({ children }) => {
	const urlParams = useParams<{ assetId: string }>();
	const { extensionClient } = useFabricContext();
	const [state, dispatch] = useReducer(IDQAPageReducer, {
		...initState,
		id: urlParams.assetId,
	});
	const i18nObj = useI18nTranslation();
	const { t } = i18nObj;

	const controller = useMemo(() => new IDQAPageController(dispatch, extensionClient, i18nObj), [extensionClient]);
	useEffect(() => {
		controller.init(urlParams.assetId);
		return () => controller.cleanUp();
	}, [controller, urlParams.assetId]);

	return (
		<Context.Provider value={{ state, controller }}>
			{state.status === API_STATUS.LOADING ? (
				<ProgressScreen icon={<DataBarVertical32Regular />} message={t(i18nKeys.main.initIDQA)} />
			) : state.status === API_STATUS.SUCCESS ? (
				children
			) : (
				<ErrorScreen message={state.errorMessage || t(i18nKeys.main.errorInitIDQA)} />
			)}
		</Context.Provider>
	);
};

export default IDQAPageContext;
